import React, { useState, useCallback, useRef, useEffect } from 'react';
import { 
  Container, 
  TextField, 
  Button, 
  Grid, 
  Typography, 
  CircularProgress,
  Box,
  Snackbar,
  Alert,
  Paper,
  InputAdornment,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
  Zoom,
  useScrollTrigger
} from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SearchIcon from '@mui/icons-material/Search';
import TaobaoProductCard from '../components/TaobaoProductCard';
import { searchTaobaoProducts, importTaobaoProduct } from '../services/taobaoService';

const TaobaoSearchPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');


  // --- FILTER ADDITION START ---
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  // --- FILTER ADDITION END ---

  // Refs for scroll handling
  const handleLoadMoreRef = useRef(null);
  
  // Back to top button visibility
  const showBackToTop = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });
  
  // Scroll to top handler
  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 12, // Using 12 as the default page size (must be <= 20 for Taobao API)
    total: 0,
    totalPages: 1,
    sort: 'default',
    hasMore: true
  });

  // Handle infinite scroll with debounce
  const handleScroll = useCallback(() => {
    // Check if we should load more items
    if (
      window.innerHeight + document.documentElement.scrollTop + 100 >= 
      document.documentElement.offsetHeight && 
      !isLoading && 
      !isLoadingMore &&
      pagination.hasMore
    ) {
      handleLoadMoreRef.current();
    }
  }, [isLoading, isLoadingMore, pagination.hasMore]);
  
  // Set up scroll event listener
  useEffect(() => {
    const scrollListener = () => handleScroll();
    window.addEventListener('scroll', scrollListener, { passive: true });
    return () => window.removeEventListener('scroll', scrollListener);
  }, [handleScroll]);

  const handleLoadMore = useCallback(async () => {
    if (isLoading || isLoadingMore || !pagination.hasMore) return;
    
    const nextPage = pagination.page + 1;
    const currentPageSize = pagination.pageSize;
    const currentSort = pagination.sort;
    
    const minPriceInFen = minPrice ? parseFloat(minPrice) * 100 : undefined;
    const maxPriceInFen = maxPrice ? parseFloat(maxPrice) * 100 : undefined;

    setIsLoadingMore(true);
    
    try {
      const searchData = await searchTaobaoProducts(searchQuery, {
        page: nextPage,
        pageSize: currentPageSize,
        sort: currentSort,
        // --- MODIFICATION START ---
        min_price: minPriceInFen,
        max_price: maxPriceInFen
        // --- MODIFICATION END ---
      });
    
      
      if (searchData?.items?.length > 0) {
        setProducts(prev => [...prev, ...searchData.items]);
        setPagination(prev => ({
          ...prev,
          page: nextPage,
          hasMore: searchData.items.length === currentPageSize,
          total: searchData.total || prev.total
        }));
      } else {
        setPagination(prev => ({
          ...prev,
          hasMore: false
        }));
      }
    } catch (err) {
      console.error('Error loading more products:', err);
      setError('Failed to load more products');
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoading, isLoadingMore, pagination.hasMore, pagination.page, pagination.pageSize, pagination.sort, searchQuery, minPrice, maxPrice]);
  // Store the latest handleLoadMore in a ref
  useEffect(() => {
    handleLoadMoreRef.current = handleLoadMore;
  }, [handleLoadMore, pagination.hasMore]);

  const handleSearch = useCallback(async (newSearch = true) => {
    if (!searchQuery.trim()) return;
    
    const currentPage = newSearch ? 1 : pagination.page;
    
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    if (!apiUrl) {
      setError('API URL is not configured. Please check your environment variables.');
      return;
    }
    
    if (newSearch) {
      setIsLoading(true);
      setError('');
      setSuccess('');
      setProducts([]);
    } else {
      setIsLoadingMore(true);
    }
    
    const minPriceInFen = minPrice ? parseFloat(minPrice) * 100 : undefined;
    const maxPriceInFen = maxPrice ? parseFloat(maxPrice) * 100 : undefined;
    
    try {
      console.log(`${newSearch ? 'Initiating' : 'Loading more'} search with query:`, searchQuery);
      
      const searchData = await searchTaobaoProducts(searchQuery, {
        page: currentPage,
        pageSize: pagination.pageSize,
        sort: pagination.sort,
        // --- MODIFICATION START ---
        min_price: minPriceInFen,
        max_price: maxPriceInFen
        // --- MODIFICATION END ---
      });
      
      console.log('Search completed. Results:', {
        itemsCount: searchData?.items?.length,
        total: searchData?.total,
        currentPage,
        hasMore: searchData?.items?.length === pagination.pageSize
      });
      
      if (searchData?.items && searchData.items.length > 0) {
        setProducts(prev => newSearch ? searchData.items : [...prev, ...searchData.items]);
        setPagination(prev => ({
          ...prev,
          page: currentPage,
          total: searchData.total || prev.total,
          hasMore: searchData.items.length === pagination.pageSize
        }));
        
        if (newSearch) {
          setSuccess(`Found ${searchData.total || searchData.items.length} products`);
        }
      } else if (newSearch) {
        console.log('No products found for query:', searchQuery);
        setError('No products found. Try a different search term or check if the product exists.');
        setProducts([]);
      }
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message || 'Failed to search Taobao. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, pagination.page, pagination.pageSize, pagination.sort, minPrice, maxPrice]);

  const handleImport = useCallback(async (product) => {
    try {
      setIsLoading(true);
      await importTaobaoProduct(product.item_url, product);
      setSuccess('Product imported successfully!');
    } catch (err) {
      console.error('Import error:', err);
      setError(err.message || 'Failed to import product');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleSortChange = (event) => {
    setPagination(prev => ({
      ...prev,
      sort: event.target.value,
      page: 1
    }));
    
    // Trigger new search with updated sort
    setTimeout(() => handleSearch(true), 0);
  };

  const handlePageSizeChange = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPagination(prev => ({
      ...prev,
      pageSize: newPageSize,
      page: 1
    }));
    
    // Trigger new search with updated page size
    setTimeout(() => handleSearch(true), 0);
  };
  
  // Remove handleKeyPress since we're using onKeyPress directly in TextField

  // handleLoadMore is now defined earlier in the file with useCallback

  return (
    <Container maxWidth={false} sx={{ 
      py: isMobile ? 2 : 4,
      px: isMobile ? 1 : 3
    }}>
      <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
          Taobao Product Search
        </Typography>
        
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search for products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch(true)}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: isLoading && (
                  <InputAdornment position="end">
                    <CircularProgress size={24} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={6} sm={4} md={2}>
            <FormControl fullWidth>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={pagination.sort}
                label="Sort By"
                onChange={handleSortChange}
                disabled={isLoading}
              >
                <MenuItem value="default">Default</MenuItem>
                <MenuItem value="price_asc">Price: Low to High</MenuItem>
                <MenuItem value="price_desc">Price: High to Low</MenuItem>
                <MenuItem value="sales_desc">Best Selling</MenuItem>
                <MenuItem value="newest">Newest</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={6} sm={4} md={2}>
            <FormControl fullWidth>
              <InputLabel>Items per page</InputLabel>
              <Select
                value={pagination.pageSize}
                label="Items per page"
                onChange={handlePageSizeChange}
                disabled={isLoading}
              >
                <MenuItem value={12}>12 per page</MenuItem>
                <MenuItem value={16}>16 per page</MenuItem>
                <MenuItem value={20}>20 per page</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={4} md={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleSearch(true)}
              disabled={isLoading || !searchQuery.trim()}
              fullWidth
              size="large"
              sx={{ height: '56px' }}
            >
              {isLoading ? 'Searching...' : 'Search'}
            </Button>
          </Grid>

          {/* --- FILTER ADDITION START --- */}
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              label="Min Price"
              type="number"
              value={minPrice}
              onChange={(e) => setMinPrice(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch(true)}
              disabled={isLoading}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              label="Max Price"
              type="number"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch(true)}
              disabled={isLoading}
            />
          </Grid>
          {/* --- FILTER ADDITION END --- */}

        </Grid>
      </Paper>

      <Box sx={{ width: '100%', my: 4 }}>
        {products.length > 0 && (
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
            Showing {Math.min(products.length, pagination.total)} of {pagination.total.toLocaleString()} products
          </Typography>
        )}
        
        <Grid container spacing={3}>
          {products.map((product) => (
            <Grid item key={`${product.id || product.itemId}-${Math.random()}`} xs={6} sm={4} md={3} lg={2} xl={2.4}>
              <TaobaoProductCard 
                product={product} 
                onImport={handleImport} 
                disabled={isLoading}
              />
            </Grid>
          ))}
          
          {/* Loading skeletons */}
          {(isLoading || isLoadingMore) && Array(pagination.pageSize).fill(0).map((_, index) => (
            <Grid item key={`loading-${index}`} xs={6} sm={4} md={3} lg={2} xl={2.4}>
              <Skeleton variant="rectangular" height={200} sx={{ mb: 1 }} />
              <Skeleton width="80%" height={20} sx={{ mb: 0.5 }} />
              <Skeleton width="60%" height={20} />
            </Grid>
          ))}
        </Grid>
        
        {!isLoading && !isLoadingMore && products.length === 0 && (
          <Box sx={{ 
            minHeight: '50vh', 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            textAlign: 'center',
            p: 4
          }}>
            <SearchIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {error || 'Search for products to get started'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try different keywords or check your search query
            </Typography>
          </Box>
        )}
        
        {/* Back to top button */}
        <Zoom in={showBackToTop}>
          <Box
            onClick={scrollToTop}
            role="button"
            tabIndex={0}
            aria-label="Back to top"
            sx={{ 
              position: 'fixed',
              bottom: 32,
              right: 32,
              zIndex: 1000,
              cursor: 'pointer',
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              borderRadius: '50%',
              width: 48,
              height: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 3,
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
              '&:focus': { 
                outline: 'none',
                boxShadow: '0 0 0 4px rgba(25, 118, 210, 0.3)'
              }
            }}
          >
            <KeyboardArrowUpIcon />
          </Box>
        </Zoom>
        
        {pagination.total > pagination.pageSize && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, width: '100%' }}>
            <Button
              variant="outlined" 
              onClick={handleLoadMore}
              disabled={isLoading || pagination.page * pagination.pageSize >= pagination.total}
              sx={{ minWidth: '200px' }}
              startIcon={isLoading ? <CircularProgress size={20} /> : null}
            >
              Load More
            </Button>
          </Box>
        )}
      </Box>


      {products.length === 0 && !isLoading && searchQuery && (
        <Box sx={{ textAlign: 'center', my: 4 }}>
          <Typography variant="h6" color="textSecondary">
            No products found. Try a different search term.
          </Typography>
        </Box>
      )}

      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setError('')} 
          severity="error" 
          sx={{ width: '100%', maxWidth: '600px' }}
          variant="filled"
        >
          {error}
        </Alert>
      </Snackbar>

      <Snackbar 
        open={!!success} 
        autoHideDuration={3000} 
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSuccess('')} 
          severity="success" 
          sx={{ width: '100%', maxWidth: '600px' }}
          variant="filled"
        >
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default TaobaoSearchPage;
